/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
/* eslint-disable jira/import-whitelist */
// eslint-disable-next-line jira/import-whitelist
import {
	TEAM_MANAGED_PROJECT,
	convertToProjectType,
} from '@jira/platform__project-types/src/project-types.tsx';

import { getProjectIdOrKey } from '@jira/router-resources__utils/src/getProjectKeyId.tsx';
import { addSpanToAll } from '@atlaskit/react-ufo/interaction-metrics';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type { ResourceStoreContext, RouterDataContext } from '@atlassian/react-resource-router';
import type { ProjectWorkflowContext } from './ProjectWorkflowContext.types.tsx';
import { QUERY } from './ProjectWorkflowContextQuery.tsx';
import type { IssueTypeEdge, ProjectResponse } from './IssueTypeEdge.types.tsx';

export const AGG_NO_PROJECT_DATA = 512;

function extractAvatarIdFromIconUrl(iconUrl: string): number {
	const regex = /\/avatar\/(\d+)/;
	const match = iconUrl.match(regex);
	if (match && match[1]) {
		return parseInt(match[1], 10);
	}
	return -1; // or handle the error as needed
}

const transformIssueTypes = (issueTypes: { edges?: IssueTypeEdge[] | null } | undefined) => {
	if (!issueTypes || !issueTypes.edges) {
		return [];
	}
	return issueTypes.edges.map(({ node }) => ({
		id: node.issueTypeId,
		name: node.name,
		iconUrl: node.avatar.large,
		avatarId: extractAvatarIdFromIconUrl(node.avatar.large),
		hierarchyLevel: node.hierarchy.level,
	}));
};

const getProjectWorkflowContextData = async (
	projectIdOrKey: string,
	cloudId: string,
): Promise<ProjectWorkflowContext> => {
	const fetchStartTime = performance.now();
	const { data, errors } = await performPostRequest<ProjectResponse>(
		'/gateway/api/graphql?operation=JiraProjectWorkflowContext',
		{
			method: 'POST',
			headers: {
				'content-type': 'application/json',
				'X-ExperimentalApi': 'JiraProject, projectStyle',
			},
			body: JSON.stringify({
				query: QUERY,
				variables: {
					cloudId,
					projectIdOrKey,
				},
			}),
		},
	);

	addSpanToAll(
		'fetch',
		'fetchProjectByKeyOrId',
		[{ name: 'network' }],
		fetchStartTime,
		performance.now(),
	);

	if (errors != null && errors.length > 0) {
		const statusCode = errors[0].extensions?.statusCode;
		throw new FetchError(
			statusCode,
			`Fetch project context via AGG failed. Errors: ${errors.map((err) => err.message).join(', ')}`,
		);
	}
	const projectData = data?.jira_projectByIdOrKey;
	if (!projectData) {
		throw new FetchError(
			AGG_NO_PROJECT_DATA,
			'Fetch project context via AGG succeeded but no data received',
		);
	}
	const { projectId, key, name, avatar, projectType, projectStyle, created } = projectData;

	return {
		key,
		name,
		id: projectId,
		avatarUrl: avatar?.medium ?? '',
		simplified: projectStyle === TEAM_MANAGED_PROJECT,
		projectType: convertToProjectType(projectType),
		issueTypes: transformIssueTypes(projectData.issueTypes),
		created,
	};
};

const getData = async (
	matchedRoute: RouterDataContext,
	{ tenantContext: { cloudId } }: ResourceStoreContext,
): Promise<ProjectWorkflowContext | null> => {
	const projectIdOrKey = getProjectIdOrKey(matchedRoute.match, matchedRoute.query);
	if (projectIdOrKey == null || projectIdOrKey === '') {
		return null;
	}
	return getProjectWorkflowContextData(projectIdOrKey, cloudId);
};

export default getData;
